// Code generated by apic; DO NOT EDIT.
import type { AuthMeta } from "../types";

/** Context values supplied at call time for sample generation. */
export interface SampleContext {
  /** Base URL of the service (e.g. "https://api.example.com"). */
  baseUrl: string;
  /** Current values for each path parameter, keyed by param name. */
  pathParams: Record<string, string>;
  /** Request body to serialize as JSON (for unsafe methods). */
  body?: unknown;
  /** Bearer token for jwt / cookie-bearer auth. */
  token?: string;
  /** API key for api_key auth. */
  apiKey?: string;
}

// ── shared helpers ────────────────────────────────────────────────────────────

/**
 * Substitute {param} placeholders in a path from ctx.pathParams.
 * Exported so callers can expand paths for non-REST surfaces too.
 */
export function expandPath(path: string, ctx: SampleContext): string {
  return path.replace(/\{([^}]+)\}/g, (_match: string, name: string): string => {
    const val = ctx.pathParams[name];
    return val !== undefined && val !== "" ? encodeURIComponent(val) : `:${name}`;
  });
}

/** Returns true for HTTP methods that carry a request body. */
export function isUnsafe(method: string): boolean {
  const m = method.toUpperCase();
  return m === "POST" || m === "PUT" || m === "PATCH" || m === "DELETE";
}

/**
 * Resolve the dominant auth mode from a RestEndpointMeta-compatible AuthMeta.
 * Walks the DNF (Disjunctive Normal Form) for composite auth to find the first
 * app-layer token mode (jwt/cookie → "jwt", api_key → "api_key").
 * Falls back to mode.toLowerCase() for single-mode endpoints.
 * Returns "mtls" when all leaves are transport-only (mtls/cac/piv).
 */
export function resolveAuthMode(auth: AuthMeta): string {
  if (auth.composite !== null && auth.dnf.length > 0) {
    // Walk DNF to find the first app-layer token mode.
    for (const group of auth.dnf) {
      for (const leaf of group) {
        const l = leaf.toLowerCase();
        if (l === "jwt" || l === "cookie") return "jwt";
        if (l === "api_key") return "api_key";
      }
    }
    // All leaves are transport-only (mtls/cac/piv). Treat as mtls.
    return "mtls";
  }
  return auth.mode.toLowerCase();
}

/** True when the auth involves an mTLS/CAC/PIV leaf (composite or single). */
export function hasMTLSLeaf(auth: AuthMeta): boolean {
  if (auth.mtlsRequired) return true;
  if (auth.composite !== null) {
    for (const group of auth.dnf) {
      for (const leaf of group) {
        const l = leaf.toLowerCase();
        if (l === "mtls" || l === "cac" || l === "piv") return true;
      }
    }
  }
  return false;
}
